// Help topics — docs-as-code content behind the contextual help drawer.
//
// Convention: a module's topic lives at src/help/topics/<module key>.md; a
// ModuleDef sets `helpTopicId` only to override (e.g. farm_clock shares the
// packhouse 'clock' topic). Coverage is therefore just file presence — a
// route gains contextual help the moment its topic file lands.
//
// Topics are raw-imported lazily via import.meta.glob, so each compiles to
// its own chunk: the service worker precaches them like any other asset and
// the drawer works fully offline after first install.
//
// Frontmatter is a flat `key: value` block — deliberately not YAML, so this
// file never needs a parser dependency:
//
//   ---
//   title: Receive a delivery      (verb-first, grade 5–6 English)
//   type: howto                    (Diátaxis: howto|tutorial|reference|explanation)
//   persona: floor                 (field|floor|admin|both)
//   priority: P0
//   ---

import { readPersisted } from '@/hooks/usePersistedState'
import { MODULES, PILOT_MODULE_TENANTS, moduleInKindView, type ModuleDef } from '@/lib/modules'

export interface HelpTopic {
  id:       string
  title:    string
  type:     string
  persona:  string
  priority: string
  /** Markdown body with the frontmatter block stripped. */
  body:     string
  /** Body rendered to HTML, ready for the drawer. */
  html:     string
}

const topicFiles = import.meta.glob('/src/help/topics/*.md', {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>

const idFromPath = (p: string) => p.replace(/^.*\//, '').replace(/\.md$/, '')

const topicIds = new Set(Object.keys(topicFiles).map(idFromPath))

export function hasHelpTopic(id: string): boolean {
  return topicIds.has(id)
}

/** Cross-cutting topics offered as "Common questions" chips on every route. */
export const COMMON_TOPIC_IDS = [
  'offline-sync', 'switch-tenant', 'account',
  // Scouting's three REVIEW surfaces live under the capture module's path,
  // so the longest-path scan handed all three the capture procedure: the '?'
  // on the printed report explained how to log an observation (audit F164).
  // They are routed by pathname below.
  'scouting-map', 'scouting-trends', 'scouting-report', 'scouting-summary',
]

/**
 * Resolve the help topic for a route. The longest matching module path wins,
 * so detail routes (/pallets/:id, /deliveries/report) inherit their list
 * page's topic. Returns undefined when no module matches or the topic file
 * doesn't exist yet — the drawer then degrades to its generic content.
 * Non-registry routes (token portals, pre-auth pages) get an explicit
 * pathname→topic map here when their topics are authored.
 */
/**
 * Routes that belong to a module but don't live under its path (the orders
 * module owns four extra routes). Checked before the module-path scan.
 */
const PATHNAME_TOPICS: [prefix: string, id: string][] = [
  ['/pack-instructions', 'orders'],
  ['/pack-instruction-print', 'orders'],
  ['/pack-ref-codes', 'orders'],
  ['/order-print', 'orders'],
  ['/orders-production-sheet', 'orders'],
  ['/products', 'orders'],
  // Farm Config's two sub-pages have no module of their own (FM12 UF-05).
  ['/farm/settings', 'farm_config'],
  ['/farm/commodities', 'farm_config'],
  // SIZA is a sub-route of the Compliance family, not a module of its own.
  ['/farm/siza', 'siza'],
  // Scouting's review surfaces — more specific than /farm/scouting, and the
  // pathname map is checked before the module-path scan.
  ['/farm/scouting/map', 'scouting-map'],
  ['/farm/scouting/trends', 'scouting-trends'],
  ['/farm/scouting/print', 'scouting-report'],
  ['/farm/scouting/summary', 'scouting-summary'],
  // Not a module: the AI-assistant sign-in page (read-only MCP pilot). Its
  // topic is ROUTED here rather than offered as a chip on every route —
  // check-help-topics.mjs counts an id named in this table as owned.
  ['/oauth/consent', 'connect-ai'],
  // Packhouse piece-work rates: a config-gated page with no module row of its
  // own (seeding a module key is a fleet-wide grant), so the module scan finds
  // nothing for it. The farm's /farm/piecework-config is not matched here.
  ['/piecework-config', 'piecework_config'],
  // The farm's rates page opens for Farm Config OR Farm Labour holders (FM04 UF-14).
  ['/farm/piecework-config', 'farm_piecework_config'],
  // Pages of the Pallets module with no tile of their own (F19 UF-17).
  ['/printing-setup', 'printing-setup'],
  ['/label-designer', 'label-designer'],
  // Quotes live under Financial but have their own pages (/quotes/:id).
  ['/quotes', 'quotes'],
]

/**
 * Home ('/') has no module of its own, and its content depends on the kind of
 * business: the topic for a kind's Home. A kind not listed has none yet.
 * check-help-topics.mjs reads the ids here, so a Home topic is never an orphan.
 */
const HOME_TOPICS: Record<string, string> = {
  FARM: 'farm_home',
}

/**
 * Config's tabs each have their own topic (U27 R3). The tab is named by ?tab=,
 * or by the tab the page last remembered (kf49.config.tab) when the address
 * names none. 'ppecb' is the old name of Business. An unknown tab falls back
 * to the hub topic 'config'. check-help-topics.mjs counts these ids as owned.
 */
const CONFIG_TAB_TOPICS: Record<string, string> = {
  health:      'config_setup',
  business:    'config_business',
  ppecb:       'config_business',
  operations:  'config_operations',
  dispatch:    'config_dispatch',
  storage:     'config_storage',
  commodities: 'config_commodities',
  admin:       'config_admin',
}

function configTabTopicId(search: string): string | undefined {
  let tab = new URLSearchParams(search).get('tab')
  if (!tab) tab = readPersisted<string | null>('kf49.config.tab', null)
  const id = tab ? CONFIG_TAB_TOPICS[tab] : undefined
  return id && topicIds.has(id) ? id : undefined
}

function routedTopicId(pathname: string): string | undefined {
  for (const [prefix, id] of PATHNAME_TOPICS) {
    if ((pathname === prefix || pathname.startsWith(prefix + '/')) && topicIds.has(id)) return id
  }
  return undefined
}

function homeTopicId(tenantKind?: string): string | undefined {
  const id = tenantKind ? HOME_TOPICS[tenantKind] : undefined
  return id && topicIds.has(id) ? id : undefined
}

export function resolveHelpTopicId(pathname: string, tenantKind?: string, search = ''): string | undefined {
  if (pathname === '/') return homeTopicId(tenantKind)
  if (pathname === '/config') {
    const tabId = configTabTopicId(search)
    if (tabId) return tabId
  }
  const routed = routedTopicId(pathname)
  if (routed) return routed
  let bestPath = ''
  let bestId: string | undefined
  for (const m of MODULES) {
    if (pathname === m.path || pathname.startsWith(m.path + '/')) {
      if (m.path.length > bestPath.length) {
        bestPath = m.path
        bestId = m.helpTopicId ?? m.key
      }
    }
  }
  return bestId && topicIds.has(bestId) ? bestId : undefined
}

/**
 * Which help topics belong in the ⌘K palette's Help group for a business of
 * `kind` in `appMode`. The scope is the KIND's catalogue in that view
 * (moduleInKindView, the same rule as the palette's Go to list) and NEVER the
 * person's own module list: a worker narrowed to a few modules, or a custom
 * role, still sees every article for their own business's pages.
 * A topic is OWNED by the modules that resolve to it (key / helpTopicId) and
 * by the modules whose path a PATHNAME_TOPICS route sits under; it is offered
 * when any owner is in the kind's catalogue. An unowned topic (offline-sync,
 * switch-tenant) is cross-cutting and always offered. An owner that is a
 * pilot module only counts for a tenant in its pilot list (`slug`). Unscoped, a farm
 * searching "fuel" was offered "Find a pallet and print its label" and "Your
 * team on BigLogistics / BigHarvest" (OR-07).
 */
export function helpTopicScope(
  kind: string | null | undefined, appMode: 'packhouse' | 'farm' = 'packhouse', slug?: string | null,
): (topicId: string) => boolean {
  // A pilot module is live for named TENANTS only (PILOT_MODULE_TENANTS), so
  // its article is offered only inside that list. A tenant gate, not a person
  // gate: everyone in a pilot business sees it, nobody outside does.
  const live = (m: ModuleDef) => {
    const pilot = PILOT_MODULE_TENANTS[m.key]
    return !pilot || (!!slug && pilot.includes(slug))
  }
  return id => {
    const owners = topicOwners(id)
    return owners.length === 0 || owners.some(m => moduleInKindView(m, kind, appMode) && live(m))
  }
}

function topicOwners(id: string): ModuleDef[] {
  const underPath = (route: string) => MODULES.filter(m => route === m.path || route.startsWith(m.path + '/'))
  return [
    ...MODULES.filter(m => (m.helpTopicId ?? m.key) === id),
    // A Config tab's topic is owned by the Config module.
    ...(Object.values(CONFIG_TAB_TOPICS).includes(id) ? MODULES.filter(m => m.key === 'config') : []),
    ...PATHNAME_TOPICS.filter(([, t]) => t === id).flatMap(([prefix]) => underPath(prefix)),
  ]
}

/** Load + parse a topic by id. Resolves to null for unknown ids. */
export async function loadHelpTopic(id: string): Promise<HelpTopic | null> {
  const entry = Object.entries(topicFiles).find(([p]) => idFromPath(p) === id)
  if (!entry) return null
  // marked is imported lazily so it stays out of the eager main bundle —
  // it compiles to its own chunk, which the SW still precaches (offline-safe).
  const [raw, { marked }] = await Promise.all([entry[1](), import('marked')])
  const { meta, body } = parseFrontmatter(raw)
  // Topics are repo-authored, PR-reviewed content compiled into the bundle —
  // the same trust level as JSX — so the rendered HTML needs no sanitiser.
  const html = marked.parse(body, { async: false }) as string
  return {
    id,
    title:    meta.title    ?? id,
    type:     meta.type     ?? 'howto',
    persona:  meta.persona  ?? 'both',
    priority: meta.priority ?? 'P1',
    body,
    html,
  }
}

/** Lightweight searchable form of a topic — no HTML render (skips marked). */
export interface HelpSearchDoc {
  id:       string
  title:    string
  body:     string
  type:     string
  persona:  string
  priority: string
}

/**
 * Load every topic's searchable text (title + body, frontmatter parsed).
 * Used to build the client-side help search index. Reads the same precached
 * topic chunks the drawer uses, so it stays offline-capable and adds no new
 * bytes to the precache — the index is built in-browser, never shipped.
 */
export async function loadAllHelpSearchDocs(): Promise<HelpSearchDoc[]> {
  const entries = Object.entries(topicFiles)
  const raws = await Promise.all(entries.map(([, load]) => load()))
  return entries.map(([path], i) => {
    const { meta, body } = parseFrontmatter(raws[i])
    const id = idFromPath(path)
    return {
      id,
      title:    meta.title    ?? id,
      body,
      type:     meta.type     ?? 'howto',
      persona:  meta.persona  ?? 'both',
      priority: meta.priority ?? 'P1',
    }
  })
}

/** The flat `key: value` block at the top of a topic — shared with the Afrikaans loader in trainingGuides.ts. */
export function parseFrontmatter(raw: string): { meta: Record<string, string>; body: string } {
  const m = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/)
  if (!m) return { meta: {}, body: raw }
  const meta: Record<string, string> = {}
  for (const line of m[1].split(/\r?\n/)) {
    const i = line.indexOf(':')
    if (i > 0) meta[line.slice(0, i).trim()] = line.slice(i + 1).trim()
  }
  return { meta, body: raw.slice(m[0].length) }
}
